import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Stack, Typography, Tab, Tabs, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails, Chip,
  FormControlLabel, Checkbox, Divider
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { useSelector } from 'react-redux'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'

import stylesheet from './questionnaire.stylesheet'
import QuestionnaireComponent from './questionnaire.component'
import DialogTitleHeaderComponent from '../patients/detail/common/dialog-title-header.component'
import CardLayoutComponent from '../patients/detail/common/card-layout.component'
import QuickAnswerButtons from './QuickAnswerButtons'
import { RootState } from '../../redux/store'
import { PatientService } from '../../services/patient.service'
import { useUpdateDataAction } from '../../utils/update-details-hook'
import Helper from '../../utils/helper'
import { formatDisplayName, hasDifferentPrefix } from '../../utils/display-name-formatter'

interface PrioritizedCondition {
  condition_id: string
  condition_name: string
  condition_display_name: string
  probability: number
  urgency: number
  dependent_questionnaires: any[]
}

interface IProps {
  open: boolean
  prioritizedConditions: PrioritizedCondition[]
  onClose: () => void
  onProbabilityUpdate?: (conditionId: string, newProbability: number) => void
  classes: ClassNameMap
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`condition-tabpanel-${index}`}
      aria-labelledby={`condition-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  )
}

const PrioritizedConditionsPanel: React.FC<IProps> = ({
  open,
  prioritizedConditions,
  onClose,
  onProbabilityUpdate,
  classes
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set())
  const [questionnaireForms, setQuestionnaireForms] = useState<Map<string, any>>(new Map())
  const [isExpandAll, setIsExpandAll] = useState(false)
  const [localAnswers, setLocalAnswers] = useState<Map<string, any>>(new Map())

  // Redux
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const patientDetailStatus = useSelector((state: RootState) => state.patientDetail.status)
  const recommended_calculators = useSelector((state: RootState) => state.commonData.recommended_calculators)

  // Services
  const patientService = useMemo(() => new PatientService(), [])
  const { updatePatientData } = useUpdateDataAction(patientDetail?.id)

  // Sort conditions by probability (descending)
  const sortedConditions = useMemo(() => {
    return [...prioritizedConditions].sort((a, b) => (b.probability || 0) - (a.probability || 0))
  }, [prioritizedConditions])

  // Reset state when panel opens
  useEffect(() => {
    if (open) {
      setActiveTab(0)
      setOpenAccordions(new Set())
      setQuestionnaireForms(new Map())
      setIsExpandAll(false)
      setLocalAnswers(new Map())
    }
  }, [open])

  // Get questionnaire from calculators
  const getQuestionnaireFromCalculators = useCallback((questionnaireId: number) => {
    const calculators = [
      ...(patientDetail?.measurements?.calculators || []),
      ...(patientDetail?.measurements?.recommended_calculators || [])
    ]
    return calculators.find((calc: any) => calc.questionnaire_id === questionnaireId)
  }, [patientDetail])

  // Check if questionnaire is recommended
  const isRecommended = useCallback((questionnaireId: number) => {
    return recommended_calculators?.some((item: any) => item.questionnaire_id === questionnaireId)
  }, [recommended_calculators])

  // Tab change handler
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }, [])

  // Advance to next condition tab
  const handleNextCondition = useCallback(() => {
    if (activeTab < sortedConditions.length - 1) {
      setActiveTab(prev => prev + 1)
    }
  }, [activeTab, sortedConditions.length])

  // Accordion toggle
  const handleAccordionToggle = useCallback((questionnaireId: string) => {
    setOpenAccordions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(questionnaireId)) {
        newSet.delete(questionnaireId)
      } else {
        newSet.add(questionnaireId)
      }
      return newSet
    })
  }, [])

  // Expand/collapse all for current tab
  const handleExpandAll = useCallback((expanded: boolean) => {
    setIsExpandAll(expanded)
    const currentCondition = sortedConditions[activeTab]
    if (!currentCondition) return

    if (expanded) {
      const allIds = currentCondition.dependent_questionnaires?.map((q: any) => q.questionnaire_id?.toString()) || []
      setOpenAccordions(new Set(allIds))
    } else {
      setOpenAccordions(new Set())
    }
  }, [sortedConditions, activeTab])

  // Handle questionnaire answer changes — initAnswer=true means the component is seeding
  // its initial state from the backend fetch; ignore these so Save buttons only enable
  // when the user has actually made a change.
  const handleQuestionnaireChange = useCallback((questionnaireId: string, answers: any, initAnswer?: boolean) => {
    if (initAnswer) return
    setQuestionnaireForms(prev => {
      const newMap = new Map(prev)
      newMap.set(questionnaireId, answers)
      return newMap
    })
  }, [])

  // Handle quick answer saves - updates local data immediately
  const handleQuickAnswerSaved = useCallback((questionnaireId: string, res: any) => {
    // Update local answers to reflect the saved answer
    setLocalAnswers(prev => {
      const newMap = new Map(prev)
      newMap.set(questionnaireId, res.first_response)
      return newMap
    })

    // Close accordion if it was open
    setOpenAccordions(prev => {
      const next = new Set(prev)
      next.delete(questionnaireId)
      return next
    })

    // Trigger data refresh
    updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }, [updatePatientData])

  // Handle quick answer changes in local-only mode (for batching)
  // questionnaireId here is the database record ID (calcInfo.id), not the questionnaire_id
  const handleQuickAnswerChanged = useCallback((questionnaireId: string, questionId: string, value: string | number) => {
    console.log('[PrioritizedPanel] handleQuickAnswerChanged:', { questionnaireId, questionId, value })

    // Update local display immediately
    setLocalAnswers(prev => {
      const newMap = new Map(prev)
      newMap.set(questionnaireId, value)
      return newMap
    })

    // Store in questionnaireForms for bulk save
    setQuestionnaireForms(prev => {
      const newMap = new Map(prev)
      newMap.set(questionnaireId, { [questionId]: { value } })
      return newMap
    })
  }, [])

  // Save single questionnaire
  const handleSaveQuestionnaire = useCallback(async (questionnaireId: string, confirmed: boolean) => {
    const answers = questionnaireForms.get(questionnaireId)
    if (!answers || Object.keys(answers).length === 0) {
      toast.warning('No changes to save', Helper.bottom_center())
      return
    }

    setIsSaving(true)
    try {
      const calcInfo = getQuestionnaireFromCalculators(parseInt(questionnaireId))
      const response = await patientService.updateQuestionnaires(patientDetail.id, {
        data: [{
          id: calcInfo?.id,
          questionnaire_id: parseInt(questionnaireId),
          confirmed,
          answer: answers,
          is_recommended: isRecommended(parseInt(questionnaireId))
        }]
      })

      if (response?.data?.message === 'Success') {
        toast.success('Questionnaire saved successfully', Helper.bottom_center())
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      } else {
        toast.error('Failed to save questionnaire', Helper.bottom_center())
      }
    } catch (error) {
      console.error('Error saving questionnaire:', error)
      toast.error('Error saving questionnaire', Helper.bottom_center())
    } finally {
      setIsSaving(false)
    }
  }, [questionnaireForms, patientDetail.id, patientService, getQuestionnaireFromCalculators, isRecommended, updatePatientData])

  // Bulk save handler (Save or Save & Confirm)
  const handleBulkSave = useCallback(async (confirmed: boolean) => {
    console.log('[PrioritizedPanel] handleBulkSave - questionnaireForms:', Array.from(questionnaireForms.entries()))

    // Get all questionnaires with changes
    const calculators = [
      ...(patientDetail?.measurements?.calculators || []),
      ...(patientDetail?.measurements?.recommended_calculators || [])
    ]
    const itemsToSave = Array.from(questionnaireForms.entries())
      .filter(([_, answers]) => answers && Object.keys(answers).length > 0)
      .map(([recordId, answers]) => {
        // Keys may be calcInfo.id (from accordions/QuickAnswer when calcInfo exists)
        // or questionnaire_id string (from QuickAnswer when calcInfo was null).
        // Try both lookups to always resolve the correct record.
        const calcInfo = calculators.find((calc: any) => calc.id === recordId)
          ?? calculators.find((calc: any) => String(calc.questionnaire_id) === String(recordId))

        return {
          id: calcInfo?.id ?? recordId,
          questionnaire_id: calcInfo?.questionnaire_id,
          confirmed,
          answer: answers,
          is_recommended: isRecommended(calcInfo?.questionnaire_id)
        }
      })

    if (itemsToSave.length === 0) {
      toast.warning('No data changes to update!', Helper.bottom_center())
      return
    }

    setIsSaving(true)
    try {
      await patientService.updateQuestionnaires(patientDetail.id, { data: itemsToSave })
      toast.success('All questionnaires updated successfully!', Helper.bottom_center())
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      setQuestionnaireForms(new Map())
      setLocalAnswers(new Map())
      onClose()
    } catch (error: any) {
      console.error('Error saving questionnaires:', error)
      if (error?.response?.data?.message?.includes('incomplete')) {
        toast.info('Answers saved (incomplete)', Helper.bottom_center())
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      } else {
        toast.error('Error saving some questionnaires', Helper.bottom_center())
      }
    } finally {
      setIsSaving(false)
    }
  }, [questionnaireForms, patientDetail, patientService, isRecommended, updatePatientData, onClose])

  // Save all and advance to next condition
  const handleSaveAndNext = useCallback(async () => {
    // Save all questionnaires in current tab that have changes
    const currentCondition = sortedConditions[activeTab]

    // Build a map of record IDs for this condition's questionnaires
    const conditionRecordIds = currentCondition?.dependent_questionnaires?.map((q: any) => {
      const calcInfo = getQuestionnaireFromCalculators(q.questionnaire_id)
      return calcInfo?.id || q.questionnaire_id?.toString()
    }) || []

    // Check if any of these have changes in questionnaireForms
    const recordIdsWithChanges = conditionRecordIds.filter((id: string) => questionnaireForms.has(id))
    const hasChanges = recordIdsWithChanges.length > 0

    if (hasChanges) {
      setIsSaving(true)
      try {
        const calculators = [
          ...(patientDetail?.measurements?.calculators || []),
          ...(patientDetail?.measurements?.recommended_calculators || [])
        ]

        const itemsToSave = recordIdsWithChanges.map((recordId: string) => {
          const answers = questionnaireForms.get(recordId)
          const calcInfo = calculators.find((calc: any) => calc.id === recordId)
          return {
            id: recordId,
            questionnaire_id: calcInfo?.questionnaire_id,
            confirmed: true,
            answer: answers,
            is_recommended: isRecommended(calcInfo?.questionnaire_id)
          }
        })

        await patientService.updateQuestionnaires(patientDetail.id, { data: itemsToSave })
        toast.success('Questionnaires saved', Helper.bottom_center())
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })

        setQuestionnaireForms(prev => {
          const newMap = new Map(prev)
          recordIdsWithChanges.forEach((id: string) => newMap.delete(id))
          return newMap
        })
      } catch (error) {
        console.error('Error saving questionnaires:', error)
        toast.error('Error saving some questionnaires', Helper.bottom_center())
      } finally {
        setIsSaving(false)
      }
    }

    // Advance to next tab
    handleNextCondition()
  }, [sortedConditions, activeTab, questionnaireForms, getQuestionnaireFromCalculators, patientDetail, patientService, isRecommended, updatePatientData, handleNextCondition])

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Arrow Right = Next condition tab
      if (e.ctrlKey && e.key === 'ArrowRight') {
        e.preventDefault()
        handleNextCondition()
      }
      // Ctrl+1-9 = Switch to specific tab
      if (e.ctrlKey && e.key >= '1' && e.key <= '9') {
        e.preventDefault()
        const tabIndex = parseInt(e.key) - 1
        if (tabIndex < sortedConditions.length) {
          setActiveTab(tabIndex)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, handleNextCondition, sortedConditions.length])

  // Get display response for questionnaire (Yes/No/scale values)
  const getDiagnosisRes = useCallback((q: any, calcInfo: any) => {
    // Use calcInfo.id as the key (same as QuickAnswerButtons uses)
    const recordId = calcInfo?.id || q.questionnaire_id?.toString()
    // Check local answers first, then fall back to calcInfo
    const localAnswer = localAnswers.get(recordId)
    const response = localAnswer ?? calcInfo?.first_response
    const isScale = calcInfo?.first_component_type === 'scale'
    const scaleValue = isScale ? parseFloat(response) : null

    // Show (Yes), (No) or scale values (1), (2), (4), (5) - not (3) or (Unknown)
    const showValue = response === 'Yes' || response === 'No' ||
      (isScale && scaleValue !== null && [1, 2, 4, 5].includes(scaleValue))

    return showValue ? `(${response})` : ''
  }, [localAnswers])

  // Render questionnaires for a condition
  const renderQuestionnaires = useCallback((condition: PrioritizedCondition) => {
    const questionnaires = condition.dependent_questionnaires || []

    if (questionnaires.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          No questionnaires linked to this condition.
        </Typography>
      )
    }

    // Sort questionnaires by display_name
    const sortedQuestionnaires = [...questionnaires].sort((a: any, b: any) =>
      (a.display_name || '').toLowerCase().localeCompare((b.display_name || '').toLowerCase())
    )

    return (
      <Stack spacing={0.5}>
        {sortedQuestionnaires.map((q: any, i: number) => {
          const calcInfo = getQuestionnaireFromCalculators(q.questionnaire_id)
          const isExpanded = openAccordions.has(q.questionnaire_id?.toString())

          // Check if we need a separator line (prefix changed from previous item)
          const prevItem = i > 0 ? sortedQuestionnaires[i - 1] : null
          const showSeparator = prevItem && hasDifferentPrefix(prevItem.display_name, q.display_name)

          // Use calcInfo.id as the consistent key (same as QuickAnswerButtons uses)
          const recordId = calcInfo?.id || q.questionnaire_id?.toString()
          // Get local answer or fall back to calcInfo
          const localAnswer = localAnswers.get(recordId)
          const firstResponse = localAnswer ?? calcInfo?.first_response

          return (
            <React.Fragment key={`q-${condition.condition_id}-${q.questionnaire_id}`}>
              {/* Light separator line when prefix changes */}
              {showSeparator && (
                <Divider sx={{ my: 0.25, borderColor: 'rgba(0, 0, 0, 0.08)', borderBottomWidth: '0.5px' }} />
              )}
              <Accordion
                square
                expanded={isExpanded}
                className={classes.patientAccrodionBg}
                sx={{ '& .MuiSvgIcon-root': { fontSize: '0.75rem !important' } }}
              >
                <AccordionSummary
                  className={classes.zeroPadding}
                  expandIcon={<ExpandMoreIcon />}
                  onClick={() => handleAccordionToggle(q.questionnaire_id?.toString())}
                >
                  <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <CardLayoutComponent
                        id={calcInfo?.id || q.questionnaire_id}
                        name={q.name}
                        input={{ cond_per_assessments: q.cond_per_assessments || calcInfo?.cond_per_assessments }}
                        leftHeader={formatDisplayName(q.display_name) || ''}
                        rightHeader={''}
                        subHeader={getDiagnosisRes(q, calcInfo)}
                        bottomText={''}
                        needs_confirmation={calcInfo?.needs_confirmation ?? undefined}
                        needs_update={calcInfo?.needs_update || undefined}
                        icons={[]}
                        programs={q.programs || []}
                        overridenPrograms={[]}
                        onRefClick={() => null}
                        onOverrideClick={() => { }}
                        is_referred={false}
                        is_overridden={false}
                        entityRef={'calculator'}
                        patientId={patientDetail.id}
                        category={'measurements'}
                      />
                    </Box>
                    <QuickAnswerButtons
                      questionnaire={{
                        id: calcInfo?.id || q.questionnaire_id?.toString(),
                        questionnaire_id: q.questionnaire_id,
                        first_component_type: calcInfo?.first_component_type || q.first_component_type,
                        first_response: firstResponse,
                        needs_confirmation: calcInfo?.needs_confirmation,
                        recent_confirmation: calcInfo?.recent_confirmation,
                        options: calcInfo?.first_options || q.first_options
                      }}
                      patientId={patientDetail.id}
                      isRecommended={isRecommended(q.questionnaire_id)}
                      onAnswerSaved={handleQuickAnswerSaved}
                      onAnswerChanged={handleQuickAnswerChanged}
                      disabled={false}
                      compact={true}
                      localOnly={true}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {calcInfo && (
                    <QuestionnaireComponent
                      data={{ isBulkEdit: true }}
                      pId={patientDetail.id}
                      qId={q.questionnaire_id}
                      qu={calcInfo.id}
                      is_recommended={isRecommended(q.questionnaire_id)}
                      isAllQuestionnarie={true}
                      handleClose={() => handleAccordionToggle(q.questionnaire_id?.toString())}
                      isSubmit={false}
                      handleChange={(qId: string, answers: any, initAnswer: boolean) => handleQuestionnaireChange(qId, answers, initAnswer)}
                      handleIsSubmit={() => {}}
                      handleSave={() => {}}
                      needsConfirmation={calcInfo.needs_confirmation}
                      presetIntent={(() => {
                        // Preview only when the quick-answer was clicked (localOnly, unsaved);
                        // a saved questionnaire returns its real answer. Never override saved data.
                        if (!localAnswers.has(recordId)) return undefined
                        const v = String(localAnswer ?? '')
                        if (['Yes', '5', '4'].includes(v)) return 'confirm'
                        if (['No', '1', '2'].includes(v)) return 'deny'
                        return undefined
                      })()}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </React.Fragment>
          )
        })}
      </Stack>
    )
  }, [patientDetail.id, openAccordions, getQuestionnaireFromCalculators, isRecommended, handleAccordionToggle, handleQuestionnaireChange, handleQuickAnswerSaved, handleQuickAnswerChanged, localAnswers, getDiagnosisRes, classes])

  if (!open || sortedConditions.length === 0) return null

  return (
    <Draggable handle=".prioritized-panel-header">
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
          onClose()
        }}
        maxWidth="md"
        fullWidth
        hideBackdrop
        disableScrollLock
        disableEscapeKeyDown
        sx={{ position: 'absolute', left: '5rem', top: '10%' }}
      >
        <DialogTitle className="prioritized-panel-header" sx={{ cursor: 'move', pb: 0 }}>
          <DialogTitleHeaderComponent
            title="Prioritized Conditions - Questionnaires"
            handleDialogClose={onClose}
          />
        </DialogTitle>

        {(isLoading || isSaving || patientDetailStatus === 'loading') && <LinearProgress />}

        <DialogContent sx={{ pt: 1, pb: 0, backgroundColor: 'var(--color-surface-muted)' }}>
          {/* Condition Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: '36px',
                '& .MuiTab-root': {
                  minHeight: '36px',
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: '0.8rem'
                }
              }}
            >
              {sortedConditions.map((condition, index) => (
                <Tab
                  key={condition.condition_id}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: activeTab === index ? 600 : 400 }}>
                        {condition.condition_display_name || condition.condition_name}
                      </Typography>
                      <Chip
                        label={`${condition.probability?.toFixed(1) || '0'}%`}
                        size="small"
                        color={activeTab === index ? 'primary' : 'default'}
                        sx={{ height: '18px', fontSize: '0.65rem' }}
                      />
                    </Stack>
                  }
                  id={`condition-tab-${index}`}
                  aria-controls={`condition-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {sortedConditions.map((condition, index) => (
              <TabPanel key={condition.condition_id} value={activeTab} index={index}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {condition.dependent_questionnaires?.length || 0} questionnaire(s) linked to {condition.condition_display_name}
                  </Typography>
                  {(condition.dependent_questionnaires?.length || 0) > 1 && (
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      control={
                        <Checkbox
                          size="small"
                          sx={{ padding: '0rem !important', transform: 'scale(0.8)' }}
                          checked={isExpandAll}
                          onChange={(e) => handleExpandAll(e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Expand/Collapse all</Typography>}
                    />
                  )}
                </Stack>
                {renderQuestionnaires(condition)}
              </TabPanel>
            ))}
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 1.5, backgroundColor: 'var(--color-surface-muted)', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="textSecondary">
            Tip: Ctrl+1-9 to switch tabs, Ctrl+→ for next
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkSave(true)}
              disabled={isSaving || questionnaireForms.size === 0}
            >
              <strong>{isSaving ? 'Saving...' : 'Save & Confirm'}</strong>
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkSave(false)}
              disabled={isSaving || questionnaireForms.size === 0}
            >
              <strong>{isSaving ? 'Saving...' : 'Save'}</strong>
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onClose}
              disabled={isSaving}
            >
              <strong>Cancel</strong>
            </Button>
            {sortedConditions.length > 1 && activeTab < sortedConditions.length - 1 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveAndNext}
                disabled={isSaving}
                endIcon={<NavigateNextIcon />}
              >
                {isSaving ? 'Saving...' : 'Next Condition'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </Draggable>
  )
}

export default withStyles(stylesheet)(PrioritizedConditionsPanel)
